import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, ShieldCheck, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const initials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?';

export const UserChip = () => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    if (!user) return null;
    const isAdmin = user.role === 'admin';
    // Client lease dashboard was retired; non-admin signed-in users land back
    // on the public home page from their menu, admins get the admin portal.
    const homePath = isAdmin ? '/admin' : '/';

    const onSignOut = async () => {
        setOpen(false);
        await logout();
        navigate('/');
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="user-chip"
                className="inline-flex items-center gap-2 border border-brand-hairline px-3 py-2 font-sans text-xs font-semibold uppercase tracking-[0.14em] text-brand-text transition-colors duration-150 hover:border-brand-gold hover:text-brand-gold"
                aria-expanded={open}
            >
                <span className="flex h-6 w-6 items-center justify-center bg-brand-gold font-display text-[10px] font-bold tabular-nums text-brand-base">
                    {initials(user.name)}
                </span>
                <span className="hidden xl:inline">{user.name.split(' ')[0]}</span>
                <ChevronDown size={14} />
            </button>

            {open && (
                <div
                    className="absolute right-0 top-[calc(100%+8px)] z-50 w-64 border border-brand-hairline bg-brand-surface shadow-xl"
                    data-testid="user-chip-menu"
                >
                    <div className="border-b border-brand-hairline px-4 py-3">
                        <p className="font-display text-sm font-bold uppercase tracking-tight text-brand-text">
                            {user.name}
                        </p>
                        <p className="mt-1 font-sans text-xs text-brand-muted truncate">
                            {user.email}
                        </p>
                        <p className="mt-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                            {isAdmin ? 'Admin' : user.company || 'Client'}
                        </p>
                    </div>
                    <Link
                        to={homePath}
                        onClick={() => setOpen(false)}
                        data-testid="user-chip-portal"
                        className="flex items-center gap-3 px-4 py-3 font-sans text-sm text-brand-text transition-colors hover:bg-brand-elevated hover:text-brand-gold"
                    >
                        {isAdmin ? <ShieldCheck size={14} /> : <User size={14} />}
                        {isAdmin ? 'Admin Portal' : 'Home'}
                    </Link>
                    <button
                        type="button"
                        onClick={onSignOut}
                        data-testid="user-chip-signout"
                        className="flex w-full items-center gap-3 border-t border-brand-hairline px-4 py-3 text-left font-sans text-sm text-brand-text transition-colors hover:bg-brand-elevated hover:text-brand-danger"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserChip;
